import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Upload, AlertTriangle, ArrowRight } from 'lucide-react'
import _ from 'lodash'
import { selectCaseById } from '../../../../store/selectors'
import { selectToast } from '../../../../store/selectors/uiSelectors'
import StatusCard from './StatusCard'
import styles from './GuidedAISidebar.module.css'

interface GuidedAISidebarProps {
  caseId: string
  className?: string
}

interface StatusHistoryEntry {
  timestamp: string
  message: string
  status: 'info' | 'warning' | 'error' | 'success'
}

const GuidedAISidebar: React.FC<GuidedAISidebarProps> = ({ caseId, className }) => {
  const loanCase = useSelector(selectCaseById(caseId))
  const verification = loanCase?.incomeVerification
  const toast = useSelector(selectToast)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)
  
  // Track status change history
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([])
  const lastStatusRef = useRef<string>('')
  const lastToastRef = useRef<string>('')

  if (!verification) { //TODO return before hooks, React hooks order violation
    return (
      <div className={`${styles.guidedSidebar} ${className || ''}`}>
        <div className={styles.emptyState}>
          <p>No verification data available</p>
        </div>
      </div>
    )
  }

  const {
    currentStep,
    status,
    workflowStatus,
    agentContext,
    activityLogs,
    incomeInsights,
    keyConcerns
  } = verification


  //TODO memoization

  // Get status severity
  const getStatusSeverity = (status: string): 'info' | 'warning' | 'error' | 'success' => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'failed':
        return 'error'
      case 'paused':
        return 'warning'
      default:
        return 'info'
    }
  }

  // Get severity level from insight
  const getSeverityLevel = (severity: string): 'info' | 'warning' | 'error' | 'success' => {
    switch (severity) {
      case 'CRITICAL':
      case 'HIGH':
        return 'error'
      case 'MEDIUM':
        return 'warning'
      case 'LOW':
        return 'info'
      default:
        return 'info'
    }
  }

  // Get waiting message based on status
  const getWaitingMessage = (status: string) => {
    switch (status) {
      case 'awaiting_docs':
        return 'Waiting for documents to be uploaded...'
      case 'awaiting_policy':
        return 'Waiting for loan officer to answer questions...'
      default:
        return null
    }
  }

  const waitingMessage = getWaitingMessage(status)

  // Track status changes and add them to history
  useEffect(() => {
    if (!currentStep) return
    
    const statusKey = `${currentStep}-${workflowStatus}`
    
    // Only add if this is a new status message
    if (lastStatusRef.current !== statusKey) {
      lastStatusRef.current = statusKey
      
      const newEntry: StatusHistoryEntry = {
        timestamp: new Date().toISOString(),
        message: _.startCase(_.toLower(currentStep)),
        status: getStatusSeverity(workflowStatus)
      }
      
      setStatusHistory(prev => [...prev, newEntry])
    }
  }, [currentStep, workflowStatus])

  // Capture toast messages and add them to history
  useEffect(() => {
    if (!toast.show || !toast.message) return
    
    const toastKey = `${toast.message}-${Date.now()}`
    
    // Only add if this is a new toast (avoid duplicates)
    if (lastToastRef.current !== toast.message) {
      lastToastRef.current = toast.message
      
      // Map toast type to severity
      const severity = toast.type === 'error' ? 'error' : 
                      toast.type === 'info' ? 'info' : 'success'
      
      const newEntry: StatusHistoryEntry = {
        timestamp: new Date().toISOString(),
        message: _.startCase(_.toLower(toast.message)),
        status: severity
      }
      
      setStatusHistory(prev => [...prev, newEntry])
      
      // Clear the reference after a delay to allow same message to appear again later
      setTimeout(() => {
        lastToastRef.current = ''
      }, 1000)
    }
  }, [toast.show, toast.message, toast.type])

  // Auto-scroll to bottom when content changes, only if last item is not fully visible
  useEffect(() => {
    if (!scrollContainerRef.current || !lastItemRef.current) return
    
    const scrollContainer = scrollContainerRef.current
    const lastItem = lastItemRef.current
    
    // Use getBoundingClientRect to get positions relative to viewport
    const containerRect = scrollContainer.getBoundingClientRect()
    const itemRect = lastItem.getBoundingClientRect()
    // Check if the last item's bottom is within the container's visible area
    // The item is fully visible if its bottom edge is at or above the container's bottom edge
    const isFullyVisible = itemRect.bottom <= containerRect.bottom
    
    // Only scroll if the last item is not fully visible
    if (!isFullyVisible) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' })
    }
  }, [activityLogs, incomeInsights, keyConcerns, agentContext, statusHistory])

  // Sort all items chronologically (oldest to newest)
  // Current Status will be added at the END (bottom)
  const allItems: Array<{
    type: 'status' | 'waiting' | 'activity' | 'nextSteps' | 'insight' | 'concern'
    timestamp?: string
    content: React.ReactNode
  }> = []

  // Iteration Progress - show first if available
  if (verification.iterationProgress) {
    allItems.push({
      type: 'status',
      timestamp: new Date().toISOString(),
      content: (
        <StatusCard
          type="activity"
          title="📊 Processing Iteration"
          message={verification.iterationProgress.message}
          severity="info"
          details={`Iteration ${verification.iterationProgress.current_iteration} of ${verification.iterationProgress.max_iterations} (${verification.iterationProgress.iteration_percentage}% complete)`}
        />
      )
    })
  }

  // Waiting State - first if applicable
  if (waitingMessage) {
    allItems.push({
      type: 'waiting',
      content: (
        <StatusCard
          type="waiting"
          title="Waiting for Action"
          message={waitingMessage}
          icon={<Upload size={18} />}
        />
      )
    })
  }

  // Status history - show all persisted status changes
  statusHistory.forEach((entry, index) => {
    allItems.push({
      type: 'status',
      timestamp: entry.timestamp,
      content: (
        <StatusCard
          key={`status-${index}`}
          type="activity"
          message={entry.message}
          timestamp={entry.timestamp}
          severity={entry.status}
        />
      )
    })
  })

  // Activity logs - oldest to newest
  if (activityLogs && activityLogs.length > 0) {
    activityLogs.slice(-20).forEach((log, index) => {
      // Check if this is an AI reasoning log
      const isAILog = log.activity.startsWith('ai_reasoning_') || 
                      log.activity.startsWith('ai_calculation_')
      
      allItems.push({
        type: 'activity',
        timestamp: log.timestamp,
        content: (
          <StatusCard
            key={`${log.timestamp}-${index}`}
            type={isAILog ? "insight" : "activity"}
            title={isAILog ? "🤖 AI Decision" : undefined}
            message={_.startCase(_.toLower(log.activity))}
            timestamp={log.timestamp}
            severity={log.status === 'completed' ? 'success' : log.status === 'failed' ? 'error' : 'info'}
            details={log.details}
          />
        )
      })
    })
  }

  // Insights
  if (incomeInsights && incomeInsights.length > 0) {
    incomeInsights
      .sort((a, b) => {
        const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
        return (severityOrder[a.severity as keyof typeof severityOrder] || 4) - 
               (severityOrder[b.severity as keyof typeof severityOrder] || 4)
      })
      .forEach((insight) => {
        allItems.push({
          type: 'insight',
          content: (
            <StatusCard
              key={insight.id}
              type="insight"
              title={insight.title}
              message={insight.whatWasFound}
              details={`${insight.whyItMatters}\n\nCalculation Impact: ${insight.calculationImpact}`}
              severity={getSeverityLevel(insight.severity)}
            />
          )
        })
      })
  }

  // Concerns
  if (keyConcerns && keyConcerns.length > 0) {
    keyConcerns.forEach((concern, idx) => {
      allItems.push({
        type: 'concern',
        content: (
          <StatusCard
            key={idx}
            type="concern"
            message={concern}
            icon={<AlertTriangle size={18} />}
          />
        )
      })
    })
  }

  // Agent Context - before current status
  if (agentContext) {
    allItems.push({
      type: 'activity',
      content: (
        <>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Current Activity</h3>
            <StatusCard
              type="activity"
              title="Agent Activity"
              message={_.startCase(_.toLower(agentContext.current_action))}
              details={agentContext.reasoning}
              confidence={agentContext.confidence}
            />
          </div>
          {agentContext.next_steps && agentContext.next_steps.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Next Steps</h3>
              <div className={styles.nextStepsSection}>
                {agentContext.next_steps.map((step, idx) => (
                  <StatusCard
                    key={idx}
                    type="activity"
                    message={_.startCase(_.toLower(step))}
                    icon={<ArrowRight size={18} />}
                    severity="info"
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )
    })
  }

  // Current Status - ALWAYS LAST (at the bottom, sticky)
  const currentStatusCard = (
    <div className={styles.stickyBottom}>
      <StatusCard
        type="current"
        title="Current Status"
        message={currentStep ? _.startCase(_.toLower(currentStep)) : 'Processing...'}
        severity={getStatusSeverity(workflowStatus)}
      />
    </div>
  )

  return (
    <div className={`${styles.guidedSidebar} ${className || ''}`}>
      <div ref={scrollContainerRef} className={styles.scrollableContent}>
        {allItems.length > 0 ? (
          allItems.map((item, index) => (
            <div 
              key={index}
              ref={index === allItems.length - 1 ? lastItemRef : undefined}
            >
              {item.content}
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>Waiting for workflow to generate activities...</p>
          </div>
        )}
      </div>
      {currentStatusCard}
    </div>
  )
}

export default GuidedAISidebar

